import * as React from 'react';
import { classNames } from '@/utils';
import { ProgressCSSProperties } from '@/types';
import { Tooltip } from '../Tooltip';
import { Icon } from '../Icon';

export interface StatCardProps {
  /** The title of the statistic. */
  title: React.ReactNode;
  /** The value of the statistic. */
  value: React.ReactNode;
  /** The description of the statistic. */
  description?: React.ReactNode;
  /** The progress value of the statistic (optional). */
  progressValue?: number;
  /** The label for the progress value (optional). */
  progressLabel?: string;
  /** Additional CSS class name for custom styling (optional). */
  className?: string;
  /** Inline styles for the StatCard (optional). */
  style?: React.CSSProperties;
}

export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>((props, ref) => {
  const { title, value, description, progressValue, progressLabel, className, style } = props;
  return (
    <article ref={ref} className={classNames('card-stat', className)} style={style}>
      <div className="title-wrapper">
        <p className="stat-title">
          <span>{title}</span>
          {description && (
            <Tooltip inline label={description}>
              <Icon className="has-tooltip">
                <i className="fa-regular fa-circle-info" />
              </Icon>
            </Tooltip>
          )}
        </p>
      </div>

      <div className="content">
        <div>
          <span className="stat-value">{value}</span>
          <small>{progressLabel}</small>
        </div>

        {progressValue >= 0 && (
          <div className="progress-container">
            <figure className="progress circular">
              <span
                className="progress-level"
                data-percent={value}
                style={{ '--percent': progressValue } as ProgressCSSProperties}
              />
            </figure>
          </div>
        )}
      </div>
    </article>
  );
});

StatCard.displayName = 'StatCard';
